import React, { useEffect, useState } from "react";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { Loader2, Coins, CreditCard, Lock, ShieldCheck } from "lucide-react";
import { GlassButton } from "@/components/GlassBits";
import { useUserAuth } from "@/context/UserAuthContext";

export const UserCredits = () => {
  const { profile } = useUserAuth();
  const [packs, setPacks] = useState(null);
  const [txns, setTxns] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [p, t] = await Promise.all([
        userApi.get("/user/credit-packs"),
        userApi.get("/user/transactions"),
      ]);
      setPacks(p.data); setTxns(t.data);
    } catch (e) {
      toast.error(apiError(e, "No se pudo cargar tu saldo"));
      setPacks({ packs: [] });
    }
  };

  useEffect(() => { load(); }, []);

  const pay = async (lookupKey) => {
    setBusy(lookupKey);
    try {
      const { data } = await userApi.post("/payments/checkout",
        { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e, "No se pudo abrir el pago"));
      setBusy(null);
    }
  };

  const askAccess = async () => {
    setBusy("access");
    try {
      await userApi.post("/user/card-access-request");
      toast.success("Le avisamos al administrador. Te habilitará el pago con tarjeta.");
    } catch (e) {
      toast.error(apiError(e, "No se pudo enviar la solicitud"));
    } finally { setBusy(null); }
  };

  if (!packs) return <Loader2 className="w-6 h-6 animate-spin text-red-400" />;
  const allowed = packs.card_payments_enabled;

  return (
    <div className="space-y-6" data-testid="user-credits">
      <div className="glass-card p-6 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/50">Saldo disponible</div>
          <div className="text-4xl font-bold text-white mt-1" data-testid="user-credit-balance">
            {profile?.credits ?? 0}
          </div>
          <div className="text-xs text-white/45 mt-1">créditos</div>
        </div>
        <Coins className="w-10 h-10 text-red-400/80" />
      </div>

      <div>
        <div className="text-white font-semibold mb-3 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-amber-300" /> Comprar créditos con tarjeta
        </div>

        {!allowed && (
          <div className="glass-card p-5 mb-4" data-testid="card-locked-card">
            <div className="flex items-center gap-2 text-white/85 text-sm">
              <Lock className="w-4 h-4 text-amber-300" /> El pago con tarjeta está desactivado en tu cuenta
            </div>
            <p className="text-xs text-white/55 mt-2 leading-relaxed">
              Solo el administrador decide quién puede pagar con tarjeta. Pídele acceso y te avisamos en cuanto lo active.
            </p>
            <div className="mt-4">
              <GlassButton tone="red" icon={ShieldCheck} loading={busy === "access"}
                data-testid="ask-card-access-btn" onClick={askAccess}>
                Pedir acceso al administrador
              </GlassButton>
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          {packs.packs.map((p) => (
            <div key={p.credits} className="glass-card p-4" data-testid={`pack-${p.credits}`}>
              <div className="text-white font-semibold">{p.credits} créditos</div>
              <div className="text-xs text-white/50 mb-3">${p.price_mxn.toLocaleString()} MXN</div>
              <GlassButton tone="red" icon={CreditCard} loading={busy === p.lookup_key}
                disabled={!allowed} data-testid={`pay-pack-${p.credits}`}
                onClick={() => pay(p.lookup_key)}>
                {allowed ? "Pagar con tarjeta" : "Necesitas permiso"}
              </GlassButton>
            </div>
          ))}
        </div>
        <p className="text-xs text-white/45 mt-3">
          Pago seguro con Stripe. Los créditos se abonan solos en cuanto el pago se confirma.
        </p>
      </div>

      {txns.length > 0 && (
        <div>
          <div className="text-white font-semibold mb-3">Movimientos</div>
          <div className="space-y-2">
            {txns.slice(0, 20).map((t) => {
              const out = ["SUB", "DEDUCT", "COMMIT", "RESERVE"].includes(t.type);
              return (
                <div key={t.txn_id || t.id} className="glass-card px-4 py-2.5 flex items-center justify-between gap-3 text-xs">
                  <span className="text-white/70 min-w-0">
                    <span className="block truncate">{t.reason || t.type}</span>
                    <span className="block text-white/40 text-[11px]">
                      {t.created_at?.slice(0, 16).replace("T", " ")}
                    </span>
                  </span>
                  <span className={`font-mono shrink-0 ${out ? "text-rose-400" : "text-emerald-400"}`}>
                    {out ? "−" : "+"}{Math.abs(t.amount)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserCredits;
